import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { useOutsideHandler } from '@phoenix/design-system/frontend/util/use-outside-handler';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { useBreakpoints } from '@phoenix/design-system/frontend/ui/breakpoints/use-breakpoints';
import type { NavItem } from '../../domain/static-content/static-content.static-generation.model';
import { PhoenixIcon } from '@phoenix/design-system/frontend/ui/phoenix-icon/phoenix-icon';

export interface NavigationProps {
  navItems?: NavItem[];
  showBackdrop?: (visible: boolean) => void;
}

const logo = getDesignSystemIcon('stihl_logo.svg');
const plusIcon = getDesignSystemIcon('icon-plus-16.svg');
const crossIcon = getDesignSystemIcon('icon-cross-16.svg');
const leftArrowIcon = getDesignSystemIcon('icon-arrow-left-16.svg');
const rightArrowIcon = getDesignSystemIcon('icon-arrow-right-16.svg');

export function Navigation({ navItems = [], showBackdrop }: NavigationProps) {
  const { t } = useTranslation('shared');
  const { lg } = useBreakpoints();

  const [activeNavItemId, setActiveNavItemId] = useState<string | undefined>();
  const [menuVisible, setMenuVisible] = useState(false);
  const wrapperRef = useRef(null);

  const disableScrolling = () => {
    document.getElementsByTagName('body')[0].classList.add('fixed');
    document.getElementsByTagName('body')[0].classList.add('overflow-hidden');
    document.getElementsByTagName('body')[0].classList.add('w-full');
  };

  const enableScrolling = () => {
    document.getElementsByTagName('body')[0].classList.remove('fixed');
    document.getElementsByTagName('body')[0].classList.remove('overflow-hidden');
    document.getElementsByTagName('body')[0].classList.remove('w-full');
  };

  useOutsideHandler(wrapperRef, () => {
    setActiveNavItemId(undefined);
    setMenuVisible(false);
    showBackdrop?.(false);
  });

  const createTopNavigationMenu = (navItem: NavItem) => (
    <a
      href={navItem.url}
      className="relative flex flex-1 items-center bg-primary-white"
      key={navItem.id}
      onClick={(e) => {
        if (navItem.type !== 'Section') return;

        // desktop & mobile - navigation first level menu entries
        if (lg) {
          if (activeNavItemId && activeNavItemId !== navItem.id) {
            setActiveNavItemId(navItem.id);
          } else {
            setActiveNavItemId(!menuVisible ? navItem.id : undefined);
            setMenuVisible(!menuVisible);
            showBackdrop?.(!menuVisible);
          }
        } else {
          if (activeNavItemId && activeNavItemId === navItem.id) {
            setActiveNavItemId(undefined);
          } else {
            setActiveNavItemId(navItem.id);
          }
        }

        e.preventDefault();
      }}
      data-testid={`nav-item_${navItem.id}`}
    >
      <div
        className={`flex h-16 flex-1 items-center px-3 stihl-text-h5-mobile lg:border-b-0 lg:border-l tablet:stihl-text-interactive-button ${
          activeNavItemId && activeNavItemId === navItem.id ? 'lg:bg-secondary-mid' : ''
        }`}
      >
        <img
          src={leftArrowIcon}
          alt="previous"
          className={`${activeNavItemId === navItem.id ? '' : 'hidden'} mr-4 h-3.5 w-3.5 lg:hidden`}
        />
        <div className="flex-1">{navItem.name}</div>
        {navItem.type === 'Section' ? (
          <motion.img
            variants={iconVariants}
            animate={activeNavItemId === navItem.id ? 'open' : 'closed'}
            transition={{ bounce: 0 }}
            alt="plus"
            className="hidden h-3.5 w-3.5 lg:block"
            src={plusIcon}
          />
        ) : (
          <PhoenixIcon className="hidden h-3.5 w-3.5 lg:block" icon={rightArrowIcon} />
        )}
        <img src={rightArrowIcon} alt="next" className={`${activeNavItemId ? 'hidden' : 'lg:hidden'} h-3.5 w-3.5`} />
      </div>
    </a>
  );

  const createNavigationMenu = (navItem?: NavItem) => {
    if (!navItem) return null;
    const hasLinks = !!navItem.children?.find((child) => child.type === 'Link');
    return (
      activeNavItemId === navItem.id && (
        <div
          data-testid="navMenuItem"
          key={navItem.id}
          className={`absolute left-0 ${activeNavItemId ? 'z-40' : 'z-20'} h-full w-full lg:overflow-y-hidden`}
        >
          <div className="lg:hidden">{createTopNavigationMenu(navItem)}</div>
          <div
            className="relative flex h-auto w-full justify-between overflow-y-auto border-b border-secondary bg-primary-white"
            data-testid={`navItem_${navItem.id}`}
            key={navItem.id}
          >
            <div className={`h-full ${lg ? 'stihl-page-container' : 'w-full'} lg:relative`}>
              <div
                className={`pb-6 pt-8 ${
                  hasLinks ? 'space-y-0.5 md:grid md:grid-cols-2 lg:grid lg:grid-cols-3' : 'justify-between lg:flex'
                }`}
              >
                {navItem.children?.map((child) => {
                  return (
                    <Link href={child.url} key={child.id} legacyBehavior>
                      <a
                        onClick={() => {
                          // links with jump out to pages mobile and desktop
                          setMenuVisible(false);
                          setActiveNavItemId(undefined);
                          showBackdrop?.(false);
                          enableScrolling();
                        }}
                        className="flex items-center justify-center"
                      >
                        <div
                          className={`mb-4 ${
                            child.type === 'Section' ? `stihl-text-h5-desktop` : 'stihl-text-interactive-link-default'
                          } flex flex-1 items-center pl-3 pr-14`}
                        >
                          <div className="pr-5">{child.name}</div>
                          {child.type === 'Section' && <img src={rightArrowIcon} alt="next" className="h-3.5 w-3.5" />}
                        </div>
                      </a>
                    </Link>
                  );
                })}
              </div>
              <a
                href="#"
                className="absolute right-0 top-0 hidden h-12 w-12 items-center justify-center bg-primary-black lg:flex"
                onClick={(e) => {
                  // close button desktop
                  setActiveNavItemId(undefined);
                  setMenuVisible(false);
                  showBackdrop?.(false);

                  e.preventDefault();
                }}
              >
                <PhoenixIcon className="h-3.5 w-3.5 text-primary-white" icon={crossIcon} />
              </a>
            </div>
          </div>
        </div>
      )
    );
  };

  return (
    <nav className="bg-gray-800 print:hidden" ref={wrapperRef} data-testid="navigation">
      <div className="border-b border-t border-secondary bg-primary-white lg:border-t-0">
        <div className={`relative ${activeNavItemId ? 'z-40' : 'z-20'} border-t border-secondary`}>
          {/* desktop */}
          <div className="hidden flex-col stihl-page-container lg:flex">
            <div className="flex w-full flex-row">
              {navItems.map(createTopNavigationMenu)}
              <img src={logo} alt="Stihl logo" className="ml-auto h-11 md:h-16" />
            </div>
          </div>
          {/* mobile */}
          <div className="flex flex-col lg:hidden">
            <div className="flex flex-row justify-between">
              {!!navItems?.length && (
                <a
                  href="#"
                  className="flex flex-1"
                  onClick={(e) => {
                    // menu main entry only mobile
                    setMenuVisible(!menuVisible);
                    setActiveNavItemId(undefined);
                    showBackdrop?.(!menuVisible);
                    !menuVisible ? disableScrolling() : enableScrolling();

                    e.preventDefault();
                  }}
                  data-testid="menu"
                >
                  <div
                    className={`flex h-11 flex-1 items-center border-l border-r border-secondary px-3 stihl-text-h5-mobile md:h-16 md:w-1/3 md:flex-initial ${
                      menuVisible ? 'bg-secondary-mid' : ''
                    }`}
                  >
                    <div className="flex-1">{t('navigation_menu')}</div>
                    <motion.img
                      src={plusIcon}
                      alt="plus"
                      className="h-3.5 w-3.5"
                      variants={iconVariants}
                      animate={menuVisible ? 'open' : 'closed'}
                      transition={{ bounce: 0 }}
                    />
                  </div>
                </a>
              )}
              <img src={logo} alt="Stihl logo" className="ml-auto h-11 md:h-16" />
            </div>
          </div>
        </div>
      </div>
      {/* desktop */}
      <div className="hidden lg:block">
        <AnimatePresence initial={false} onExitComplete={enableScrolling} mode="wait">
          {activeNavItemId && (
            <motion.div
              data-testid="navigationMenu"
              className={`absolute left-0 ${activeNavItemId ? 'z-40' : 'z-20'} w-full`}
              key="navigationMenu"
              initial="closed"
              exit="closed"
              variants={navigationMenuVariants}
              animate="open"
              transition={transition}
            >
              {createNavigationMenu(navItems.find(({ id }) => activeNavItemId === id))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* mobile */}
      <AnimatePresence initial={false} onExitComplete={enableScrolling} mode="wait">
        {menuVisible && (
          <motion.div
            variants={mobileMenuVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="absolute z-40 h-full w-full lg:hidden"
            transition={transition}
          >
            {navItems.map(activeNavItemId ? createNavigationMenu : createTopNavigationMenu)}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

const navigationMenuVariants = {
  open: { height: 'calc(100% + 65px)' },
  closed: { height: '0' },
};

const iconVariants = {
  open: { rotate: 45 },
  closed: { rotate: 0 },
};

const mobileMenuVariants = {
  open: { right: 0 },
  closed: { right: '-100%' },
  exit: { right: 0 },
};

const transition = { duration: 0.5, ease: [0.04, 0.62, 0.23, 0.98] };
